import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { localToday } from "../lib/constants";

const queryParams = (filters) => {
  const params = Object.fromEntries(Object.entries(filters).filter(([k, v]) => v && (v !== "all" || k === "potential_of")));
  if (params.followup === "due") params.today = localToday();
  return params;
};

export function useLeadsData(filters) {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadLeads = useCallback(async () => {
    try {
      const params = queryParams(filters);
      const [l, s] = await Promise.all([api.get("/leads", { params }), api.get("/stats", { params })]);
      setLeads(l.data);
      setStats(s.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(loadLeads, filters.q ? 300 : 0);
    return () => clearTimeout(t);
  }, [loadLeads, filters.q]);

  return { leads, setLeads, stats, loading, loadLeads };
}

export function useScans(onFinished) {
  const [scans, setScans] = useState([]);
  const wasRunning = useRef(false);
  const loadScans = useCallback(() => api.get("/scans").then((r) => setScans(r.data)).catch((e) => console.warn("scans", e)), []);
  useEffect(() => { loadScans(); }, [loadScans]);

  const running = scans.some((s) => s.status === "running");
  useEffect(() => {
    if (wasRunning.current && !running) {
      onFinished();
      toast.success("Scan finished");
    }
    wasRunning.current = running;
    if (!running) return;
    const t = setInterval(loadScans, 4000);
    return () => clearInterval(t);
  }, [running, loadScans, onFinished]);

  return { scans, setScans, running };
}

export function useTeam() {
  const [team, setTeam] = useState([]);
  const loadTeam = useCallback(() => api.get("/team").then((r) => setTeam(r.data)).catch((e) => console.warn("team", e)), []);
  useEffect(() => { loadTeam(); }, [loadTeam]);
  const teamById = useMemo(() => Object.fromEntries(team.map((m) => [m.id, m])), [team]);
  return { team, teamById, loadTeam };
}

export function useSelection(leads) {
  const [selected, setSelected] = useState({});
  const toggle = (id) => setSelected((s) => {
    const n = { ...s };
    if (n[id]) delete n[id];
    else n[id] = leads.find((l) => l.id === id);
    return n;
  });
  const allShown = leads.length > 0 && leads.every((l) => selected[l.id]);
  const toggleAllShown = () => setSelected((s) => {
    const n = { ...s };
    leads.forEach((l) => (allShown ? delete n[l.id] : (n[l.id] = l)));
    return n;
  });
  return { selected, selectedIds: Object.keys(selected), toggle, allShown, toggleAllShown, clear: () => setSelected({}) };
}

export function useMyLeads(user, team, filters, setFilters, leads) {
  const me = useMemo(
    () => team.find((m) => m.name.trim().toLowerCase() === (user?.name || "").trim().toLowerCase()),
    [team, user]
  );
  const myView = !!me && filters.assigned === me.id;
  const [myDue, setMyDue] = useState(0);
  const meId = me?.id;
  useEffect(() => {
    if (!meId) return setMyDue(0);
    api.get("/leads", { params: { assigned: meId, followup: "due", today: localToday() } })
      .then((r) => setMyDue(r.data.length)).catch((e) => console.warn("my due", e));
  }, [meId, leads]);
  const toggle = () => {
    if (!me) {
      toast.info(`Add “${user?.name}” to the Team list (same name) to get your own leads view.`);
      return;
    }
    setFilters((f) => ({ ...f, assigned: myView ? "all" : me.id }));
  };
  const shownLeads = useMemo(
    () => (myView ? [...leads].sort((a, b) => (a.follow_up_date || "9999").localeCompare(b.follow_up_date || "9999")) : leads),
    [myView, leads]
  );
  return { myView, myDue, toggle, shownLeads };
}
